/**
 * Графік заряду/розряду УЗЕ + Підсумковий прогноз (сонячна генерація + УЗЕ).
 *
 * Логіка й ендпоінти ті самі, що на сторінці «Передача до MMS» — там віджет
 * лишається без змін. Тут окрема копія в темній палітрі цієї сторінки: спільних
 * компонентів свідомо не заводимо, щоб правки тут не зачіпали робочий екран.
 *
 * Розділ показується лише для активів з УЗЕ: якщо `config` порожній
 * (у asset_bess_systems немає активного запису), компонент нічого не рендерить.
 *
 * Після збереження графіка обидва запити перечитуються, тож підсумковий прогноз
 * одразу відображає введені заряд і розряд.
 *
 * Погодинні деталі обох віджетів розкриваються однією кнопкою: сітка вводу і
 * таблиця під графіком — це той самий зріз по годинах, тож ховати їх окремо
 * означало б лишати половину картини.
 */
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { API_BASE_URL, authFetch } from '@/services/api';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { useFcmmsTheme } from '../theme';
import { Loader2, Save, Sparkles, Table2, type LucideIcon } from 'lucide-react';
import { useChartTheme } from '@/components/base/chartTheme';
import ChartLegend from '@/components/base/ChartLegend';

type Mode = 'idle' | 'charge' | 'discharge';

interface BessCfg { maxPowerKw: number | null; usableKwh: number | null; manufacturer?: string; model?: string }

/** Регламент технічної експлуатації УЗЕ — приходить з бекенду (єдине джерело). */
interface BessLimits {
  minSocPct: number; maxSocPct: number; chargeLossK: number; dischargeGridK: number;
  /** Мінімальна робоча потужність установки, кВт. null — у РП станції не задана. */
  minPowerKw: number | null;
}
/** past/today — доба вже відпрацьована (запис факту); future — план. */
type DayKind = 'past' | 'today' | 'future';
interface BessSoc {
  pct: number | null; kwh: number | null;
  source: 'live' | 'history' | 'stale' | 'fault' | 'none' | string;
  readAtLocal: string | null; ageSec: number | null; anyFault?: boolean;
}
const DEFAULT_LIMITS: BessLimits = { minSocPct: 18, maxSocPct: 99, chargeLossK: 1.15, dischargeGridK: 1.05, minPowerKw: null };
interface NetHour { hourLocal: string; pvKwh: number; chargeKwh: number; dischargeKwh: number; netKwh: number }
interface NetResp { pvKwh: number; chargeKwh: number; dischargeKwh: number; netKwh: number; hours: NetHour[] }

interface Props { assetId: string; date: string; canEdit: boolean; onChanged?: () => void }

// Semantic chart colours — theme-independent.
// Кольори серій беруться зі стандарту (chartTheme) у самому компоненті.

const RED = '#5E656D';

/**
 * Геометрія графіка. Ті самі числа передано у <YAxis width> і <ComposedChart
 * margin>, і ними ж відступає таблиця під графіком — так її 24 колонки стають
 * рівно над поділками осі X. Якщо міняти одне, міняти й друге.
 */
const PLOT_LEFT = 52;
const PLOT_RIGHT = 10;

const n = (v: number | null | undefined, d = 1) =>
  v == null ? '—' : Number(v).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d });

/**
 * Плавне згортання без заміру висоти: рядок сітки їде з 0fr до 1fr, тож браузер
 * анімує саме ту висоту, яку вміст займе насправді — і сітка годин, і таблиця
 * ростуть як є, без магічних чисел і без стрибка наприкінці.
 *
 * Вміст лишається в дереві, щоб було що анімувати, тому згорнутий блок
 * позначаємо inert: інакше поля вводу ловили б фокус із клавіатури, будучи
 * невидимими.
 */
function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div aria-hidden={!open}
      style={{
        display: 'grid',
        gridTemplateRows: open ? '1fr' : '0fr',
        opacity: open ? 1 : 0,
        transition: 'grid-template-rows .3s cubic-bezier(.4,0,.2,1), opacity .22s ease',
      }}>
      <div style={{ overflow: 'hidden', minHeight: 0 }} {...(open ? {} : { inert: '' as any })}>
        {children}
      </div>
    </div>
  );
}

export default function BessSchedule({ assetId, date, canEdit, onChanged }: Props) {
  const { t } = useTranslation();
  const ct = useChartTheme();
  const T = useFcmmsTheme();
  // Розряд УЗЕ — токен --ok-2 (другий зелений: віддача в мережу).
  const C_DISCHARGE = T.ok2;
  const [cfg, setCfg] = useState<BessCfg | null>(null);
  const [limits, setLimits] = useState<BessLimits>(DEFAULT_LIMITS);
  const [soc, setSoc] = useState<BessSoc | null>(null);
  const [dayKind, setDayKind] = useState<DayKind>('future');
  // Стартовий SOC розрахунку. null → беремо живу телеметрію. Диспетчер може
  // перекрити: для доби D+1 «зараз» і «на 00:00 завтра» — різні стани.
  const [startSocOverride, setStartSocOverride] = useState<number | null>(null);
  const [bess, setBess] = useState<Record<number, { mode: Mode; powerKw: number }>>({});
  const [net, setNet] = useState<NetResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string>('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err' | 'warn'; text: string } | null>(null);
  const [hoursOpen, setHoursOpen] = useState(false);

  const load = useCallback(() => {
    if (!assetId || !date) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      authFetch(`${API_BASE_URL}/comdisp/bess-schedule?assetId=${assetId}&date=${date}`).then((r) => (r.ok ? r.json() : null)),
      authFetch(`${API_BASE_URL}/comdisp/net-forecast?assetId=${assetId}&date=${date}`).then((r) => (r.ok ? r.json() : null)),
    ]).then(([sched, nf]) => {
      setCfg(sched?.config ?? null);
      setLimits(sched?.limits ?? DEFAULT_LIMITS);
      setSoc(sched?.soc ?? null);
      setDayKind((sched?.dayKind as DayKind) ?? 'future');
      const m: Record<number, { mode: Mode; powerKw: number }> = {};
      (sched?.hours || []).forEach((h: any) => { m[h.hour] = { mode: h.mode, powerKw: h.powerKw }; });
      setBess(m);
      setNet(nf ?? null);
    }).catch(() => { setCfg(null); setNet(null); setSoc(null); })
      .finally(() => setLoading(false));
  }, [assetId, date]);

  useEffect(() => { setMsg(null); load(); }, [load]);
  // Ручний стартовий SOC скидаємо лише при зміні активу або доби: інакше
  // введене значення зникало після кожного збереження (load() перечитує дані).
  useEffect(() => { setStartSocOverride(null); }, [assetId, date]);

  const setHour = (hour: number, patch: Partial<{ mode: Mode; powerKw: number }>) =>
    setBess((p) => ({ ...p, [hour]: { mode: 'idle', powerKw: 0, ...p[hour], ...patch } }));

  /** Стартовий SOC розрахунку: ручний override → жива телеметрія → нижня межа. */
  const startPct = startSocOverride != null ? startSocOverride
    : (soc?.pct != null ? soc.pct : limits.minSocPct);

  /**
   * ── АЛГОРИТМ ПЕРЕВІРКИ ГРАФІКА УЗЕ (регламент техексплуатації) ───────────
   *
   * Крок 0. Стартовий стан: SOC береться з ЖИВОЇ телеметрії (середнє по 16 PCS
   *         за останній цикл), а не «доба починається з порожньої батареї», як
   *         було раніше. Саме через те припущення вечірній розряд відхилявся
   *         навіть тоді, коли УЗЕ фізично заряджена.
   *
   * Крок 1. Потужність години ≤ паспортної (16 МВт).
   *
   * Крок 2. Заряд. У батарею потрапляє команда / 1,15 (втрати заряду ~13%).
   *         Заборонено виходити вище SOCmax = 99%: останній відсоток батарея
   *         добирає в CV-режимі повільно, у план його не закладаємо.
   *
   * Крок 3. Розряд. З батареї йде рівно команда (у точці приєднання це
   *         команда / 1,05 — трансформатори УЗЕ). Заборонено опускатись нижче
   *         SOCmin = 18% — робоча межа, підтверджена тижнями експлуатації
   *         (паспортні 15% лишаються аварійним запасом, не плановим).
   *
   * Крок 4. Стан переноситься на наступну годину; траєкторія SOC показується
   *         під кожною годиною, щоб диспетчер бачив, ЧОМУ година не проходить.
   */
  const check = useMemo(() => {
    const maxP = cfg?.maxPowerKw ?? Infinity;
    const usable = cfg?.usableKwh ?? null;
    const errByHour: Record<number, string> = {};
    const msgs: string[] = [];
    const trace: Record<number, number> = {};
    if (usable == null) return { errByHour, msgs, trace, endPct: null as number | null, roomKwh: null as number | null, availKwh: null as number | null };

    const pmin = limits.minPowerKw;
    const socMinKwh = (limits.minSocPct / 100) * usable;
    const socMaxKwh = (limits.maxSocPct / 100) * usable;
    let socKwh = Math.max(0, Math.min(usable, (startPct / 100) * usable));
    const pct = (v: number) => Math.round((v / usable) * 1000) / 10;
    const r0 = (v: number) => Math.round(v).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA');

    for (let h = 1; h <= 24; h++) {
      const cell = bess[h] || { mode: 'idle' as Mode, powerKw: 0 };
      const hh = String(h).padStart(2, '0');
      if (cell.mode !== 'idle' && cell.powerKw > maxP + 1e-6) {
        errByHour[h] = t('ak_dsp4_above_the_rated_power_kw', { p: r0(maxP) });
        msgs.push(t('ak_dsp4_hr_power_kw_is_above_the_rated_power_k', { hh, p: r0(cell.powerKw), max: r0(maxP) }));
      }
      // Pmin — нижня межа керованого діапазону (РП станції). Команду нижче неї
      // установка не виконає, а в MMS вона перетворюється на відхилення.
      // Для МИНУЛОЇ доби не перевіряємо: там у клітинці не команда, а факт, і
      // годинне середнє законно буває малим, якщо PCS працював кілька хвилин.
      if (pmin != null && dayKind !== 'past'
          && cell.mode !== 'idle' && cell.powerKw > 1e-6 && cell.powerKw < pmin - 1e-6) {
        errByHour[h] = errByHour[h] || t('ak_dsp4_below_pmin_kw', { p: r0(pmin) });
        const modeWord = cell.mode === 'charge' ? t('ak_dsp4_charge') : t('ak_dsp4_discharge');
        msgs.push(t('ak_dsp4_hr_kw_is_below_the_minimum_operating_p', { hh, mode: modeWord, p: r0(cell.powerKw), pmin: r0(pmin), miss: r0(pmin - cell.powerKw) }));
      }
      if (cell.mode === 'charge') {
        // Скільки команди ще влізе до стелі SOC (з урахуванням втрат заряду).
        const roomCmd = Math.max(0, (socMaxKwh - socKwh) * limits.chargeLossK);
        if (cell.powerKw > roomCmd + 1e-6) {
          errByHour[h] = errByHour[h] || t('ak_dsp4_overcharge_kwh_free', { r: r0(roomCmd) });
          msgs.push(t('ak_dsp4_hr_charge_kwh_does_not_fit_at_soc_ther', { hh, p: r0(cell.powerKw), soc: pct(socKwh), max: limits.maxSocPct, room: r0(roomCmd), over: r0(cell.powerKw - roomCmd) }));
        }
        socKwh = Math.min(socMaxKwh, socKwh + cell.powerKw / limits.chargeLossK);
      } else if (cell.mode === 'discharge') {
        // Доступно до робочої підлоги SOC — саме стільки можна віддати командою.
        const availCmd = Math.max(0, socKwh - socMinKwh);
        if (cell.powerKw > availCmd + 1e-6) {
          errByHour[h] = errByHour[h] || t('ak_dsp4_below_the_limit_kwh_available', { min: limits.minSocPct, a: r0(availCmd) });
          msgs.push(t('ak_dsp4_hr_discharge_kwh_takes_the_bess_below', { hh, p: r0(cell.powerKw), min: limits.minSocPct, soc: pct(socKwh), a: r0(availCmd), miss: r0(cell.powerKw - availCmd) }));
        }
        socKwh = Math.max(socMinKwh, socKwh - cell.powerKw);
      }
      trace[h] = pct(socKwh);
    }
    const startKwh = Math.max(0, Math.min(usable, (startPct / 100) * usable));
    return {
      errByHour, msgs, trace,
      endPct: pct(socKwh),
      roomKwh: Math.max(0, (socMaxKwh - startKwh) * limits.chargeLossK),
      availKwh: Math.max(0, startKwh - socMinKwh),
    };
  }, [bess, cfg, limits, startPct, dayKind, t]);

  /**
   * 🔒 ПРАВИЛО (рішення власника процесу, 2026-09-28):
   *
   *   минула доба  → КОРИГУВАТИ МОЖНА. Це запис того, що вже сталося, і
   *     модельні коефіцієнти не мають права ветувати виміряний факт: графік
   *     26.09 не проходив перевірку на 132 кВт·год (0,36% ємності) через
   *     коефіцієнт заряду 1,15 — хоча той розряд реально відбувся і батарея
   *     вийшла на свої 18%. Тут перевірка лише попереджає.
   *
   *   поточна й майбутня → ЗБЕРЕГТИ НЕ МОЖНА, доки баланс не сходиться. Це
   *     не звіт, а завдання на роботу установки: невалідні числа підуть в
   *     диспетчеризацію і здатні нашкодити УЗЕ (розряд нижче робочої межі,
   *     заряд у стелю). Блок тут навмисний і знімати його не можна.
   *
   * Перевірка поточної доби стала коректною після того, як стартовий SOC
   * почали брати на 00:00 з історії: доти живий SOC опівдні вбирав денний
   * заряд, і той заряд рахувався двічі.
   */
  const blocking = dayKind !== 'past';

  // Помилку не можна виправити в згорнутій сітці — тож розкриваємо самі.
  useEffect(() => { if (check.msgs.length > 0) setHoursOpen(true); }, [check.msgs.length]);

  const save = async () => {
    setBusy('save'); setMsg(null);
    try {
      const hours = Object.entries(bess).map(([h, v]) => ({ hour: Number(h), mode: v.mode, powerKw: v.powerKw }));
      const r = await authFetch(`${API_BASE_URL}/comdisp/bess-schedule`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, date, setBy: 'forecast-mms-ui', hours }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `HTTP ${r.status}`);
      setMsg({ kind: 'ok', text: t('ak_dsp4_bess_schedule_saved_the_net_forecast_h') });
      load(); onChanged?.();
    } catch (e: any) { setMsg({ kind: 'err', text: e?.message || t('ak_dsp4_failed_to_save') }); }
    finally { setBusy(''); }
  };

  const autofill = async () => {
    setBusy('ai'); setMsg(null);
    try {
      const r = await authFetch(`${API_BASE_URL}/comdisp/bess-autofill`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, date }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b?.message || `HTTP ${r.status}`);
      const m: Record<number, { mode: Mode; powerKw: number }> = {};
      (b.hours || []).forEach((h: any) => { m[h.hour] = { mode: h.mode, powerKw: h.powerKw }; });
      setBess(m);
      setHoursOpen(true);  // запропоноване треба переглянути перед збереженням
      setMsg({ kind: b.socSource === 'live' ? 'ok' : 'warn', text: b.note || t('ak_dsp4_schedule_proposed_review_and_save_it') });
    } catch (e: any) { setMsg({ kind: 'warn', text: e?.message || t('ak_dsp4_autofill_unavailable') }); }
    finally { setBusy(''); }
  };

  const chartData = useMemo(() => (net?.hours || []).map((h) => ({
    hour: (h.hourLocal || '').slice(11, 13) || h.hourLocal,
    sun: h.pvKwh / 1000,
    charge: -Math.abs(h.chargeKwh) / 1000,
    discharge: h.dischargeKwh / 1000,
    net: h.netKwh / 1000,
  })), [net]);

  // Активи без УЗЕ цей розділ не показують узагалі.
  if (loading) return null;
  if (!cfg) return null;

  // Кнопки шапки — квадратні монохромні іконки зі стандарту §6 (36 / radius 10).
  // Підпис пішов у title та aria-label: три слова поспіль розпирали шапку.
  // Брендового червоного на «Зберегти» більше немає: збереження графіка — дія
  // звичайна, а не помилка (§2), тож усі три кнопки однакові на вигляд.
  // Стан «виконується» показує той самий квадрат, лише з крутілкою замість
  // іконки, — так ряд не смикається по ширині.
  const iconBtn = (
    Icon: LucideIcon,
    onClick: () => void,
    opts: { disabled?: boolean; busy?: boolean; active?: boolean; title: string },
  ) => (
    <button onClick={onClick} disabled={opts.disabled} title={opts.title} aria-label={opts.title}
      className="inline-flex items-center justify-center transition-colors shrink-0"
      style={{
        width: 36, height: 36, borderRadius: 10,
        background: T.innerBg,
        color: opts.disabled ? T.textDim : T.textMain,
        // Відкритий стан позначає контур, а не заливка — як в інших
        // сегментованих контролах кабінету.
        border: `1px solid ${opts.active ? 'var(--line-strong)' : T.cardBorder}`,
        cursor: opts.disabled ? 'not-allowed' : 'pointer',
        opacity: opts.disabled ? 0.55 : 1,
      }}>
      {opts.busy
        ? <Loader2 size={16} strokeWidth={2} className="animate-spin" />
        : <Icon size={16} strokeWidth={2} />}
    </button>
  );

  return (
    <>
      {/* ── редактор графіка УЗЕ ─────────────────────────────────────────── */}
      <div className="rounded-xl p-4 sm:p-5 mb-5" style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}` }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-base font-bold" style={{ color: T.textMain }}>{t('ak_dsp4_bess_charge_discharge_schedule')}</div>
            <div className="text-[11px] mt-0.5" style={{ color: T.textMuted }}>
              {t('ak_dsp4_charging_reduces_discharging_increases')}
              {cfg.maxPowerKw != null && ` · ${n(cfg.maxPowerKw, 0)} ${t('ak_dsp4_kw')}`}
              {cfg.usableKwh != null && ` / ${n(cfg.usableKwh, 0)} ${t('ak_dsp4_kwh')}`}
              {cfg.manufacturer ? ` · ${cfg.manufacturer}` : ''}
            </div>
            {/* Стан УЗЕ ПЕРЕД внесенням даних: без нього графік складається
                наосліп, а перевірка не має від чого відштовхуватись. */}
            <div className="text-[11px] mt-1 flex items-center gap-2 flex-wrap">
              <span style={{ color: T.textMuted }}>
                {soc?.source === 'history' ? t('ak_dsp4_bess_charge_at_start_of_day') : t('ak_dsp4_bess_charge_now')}
              </span>
              <span className="font-semibold" style={{ color: soc?.source === 'live' ? T.textMain : T.textMuted }}>
                {soc?.pct != null ? `${n(soc.pct, 1)}%` : t('ak_dsp4_no_telemetry')}
                {soc?.kwh != null ? ` · ${n(soc.kwh, 0)} ${t('ak_dsp4_kwh')}` : ''}
              </span>
              {soc?.readAtLocal && (
                <span style={{ color: T.textMuted }}>
                  · {soc.readAtLocal.slice(11, 16)}
                  {soc.source === 'history' ? t('ak_dsp4_from_telemetry_history')
                    : soc.source === 'stale' ? t('ak_dsp4_stale')
                    : soc.source === 'fault' ? t('ak_dsp4_pcs_in_fault') : ''}
                </span>
              )}
              <span style={{ color: T.textMuted }}>
                {t('ak_dsp4_working_range', { a: limits.minSocPct, b: limits.maxSocPct })}
                {limits.minPowerKw != null ? ` · Pmin ${limits.minPowerKw} ${t('ak_dsp4_kw')}` : ''}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canEdit && iconBtn(Sparkles, autofill,
              { disabled: !!busy, busy: busy === 'ai', title: t('ak_dsp4_autofill_propose_a_schedule_automatica') })}
            {canEdit && iconBtn(Save, save,
              { disabled: !!busy || (blocking && check.msgs.length > 0), busy: busy === 'save', title: t('ak_dsp4_save_bess_schedule') })}
            {/* Погодинні дані — таблиця з розкладкою по годинах, тож і значок
                таблиці, а не стрілка: він каже, ЩО відкриється, а не куди. */}
            {iconBtn(Table2, () => setHoursOpen((v) => !v),
              { active: hoursOpen, title: hoursOpen ? t('ak_dsp4_collapse_hourly_data') : t('ak_dsp4_hourly_data') })}
          </div>
        </div>

        <Collapse open={hoursOpen}>
          <div style={{ paddingTop: 12 }}>
            {/* Стартовий SOC розрахунку + скільки від нього можна взяти/додати. */}
            <div className="text-[11px] mb-3 rounded-lg px-3 py-2 flex items-center gap-x-4 gap-y-1.5 flex-wrap"
              style={{ background: T.innerBg, border: `1px solid ${T.cardBorder}`, color: T.textMuted }}>
              <span className="flex items-center gap-1.5">
                {t('ak_dsp4_start_soc')}
                <input type="number" min={0} max={100} step={1} disabled={!canEdit}
                  value={startSocOverride != null ? startSocOverride : (soc?.pct != null ? Math.round(soc.pct * 10) / 10 : limits.minSocPct)}
                  onChange={(e) => setStartSocOverride(Math.max(0, Math.min(100, Number(e.target.value))))}
                  style={{ width: 62, background: T.cardBg, color: T.textMain, border: `1px solid ${T.cardBorder}`,
                           borderRadius: 5, fontSize: 11, padding: '2px 4px', colorScheme: T.inputScheme }} />
                %
                {startSocOverride != null && (
                  <button type="button" onClick={() => setStartSocOverride(null)}
                    style={{ color: T.textMuted, textDecoration: 'underline', fontSize: 10 }}>
                    {t('ak_dsp4_restore_telemetry')}
                  </button>
                )}
              </span>
              <span>{t('ak_dsp4_can_charge')} <b style={{ color: T.textMain }}>{n(check.roomKwh, 0)}</b> {t('ak_dsp4_kwh')}</span>
              <span>{t('ak_dsp4_can_discharge')} <b style={{ color: T.textMain }}>{n(check.availKwh, 0)}</b> {t('ak_dsp4_kwh')}</span>
              <span>{t('ak_dsp4_soc_at_end_of_day')} <b style={{ color: T.textMain }}>{n(check.endPct, 1)}%</b></span>
              <span style={{ opacity: .8 }}>
                {t('ak_dsp4_charge_losses_discharge_at_the_connect', { c: limits.chargeLossK, d: limits.dischargeGridK })}
                {limits.minPowerKw != null ? ` · ${t('ak_dsp4_command_from_kw', { p: limits.minPowerKw })}` : ''}
              </span>
            </div>

            {msg && (
              <div className="text-xs mb-3 rounded-lg px-3 py-2"
                style={{
                  background: msg.kind === 'ok' ? 'rgba(46,157,83,0.10)' : msg.kind === 'warn' ? 'rgba(166,87,91,0.10)' : 'rgba(199,122,27,0.10)',
                  border: `1px solid ${msg.kind === 'ok' ? 'rgba(46,157,83,0.35)' : msg.kind === 'warn' ? 'rgba(166,87,91,0.4)' : 'rgba(199,122,27,0.35)'}`,
                  color: msg.kind === 'ok' ? T.bannerOkText : msg.kind === 'warn' ? T.bannerWarnText : T.bannerErrText,
                }}>{msg.text}</div>
            )}

            {check.msgs.length > 0 && (
              <div className="text-xs mb-3 rounded-lg px-3 py-2"
                style={blocking
                  ? { background: 'rgba(94,101,109,0.10)', border: `1px solid ${RED}55`, color: T.bannerErrText }
                  // Минула доба: бурштиновий, як усі попередження кабінету —
                  // червоний лишається за реальною помилкою (догма §3).
                  : { background: 'rgba(199,122,27,0.10)', border: `1px solid ${T.warn}55`, color: T.bannerWarnText }}>
                <div className="flex items-center gap-1.5 font-semibold">
                  <i className={blocking ? 'ri-error-warning-line' : 'ri-information-line'} />
                  {blocking
                    ? t('ak_dsp4_technically_impossible_values_fix_them')
                    : t('ak_dsp4_the_bess_balance_does_not_add_up_for_a')}</div>
                <ul className="mt-1 ml-4 list-disc">
                  {check.msgs.slice(0, 6).map((m, i) => <li key={i}>{m}</li>)}
                </ul>
                {check.msgs.length > 6 && <div className="mt-1">{t('ak_dsp4_and_more', { n: check.msgs.length - 6 })}</div>}
              </div>
            )}

            {/* Дванадцять годин у рядку: доба лягає у два однакові ряди 01–12 і
                13–24. На вузькому екрані ряди не ламаються, а прокручуються. */}
            <div className="overflow-x-auto">
              <div className="grid gap-1.5" style={{ gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', minWidth: 780 }}>
                {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => {
                  const cur = bess[h] || { mode: 'idle' as Mode, powerKw: 0 };
                  const err = check.errByHour[h];
                  const errCol = blocking ? RED : T.warn;
                  const col = err ? errCol : cur.mode === 'charge' ? ct.series.warn : cur.mode === 'discharge' ? C_DISCHARGE : T.cardBorder;
                  return (
                    <div key={h} title={err || undefined}
                      style={{ border: `1px solid ${col}`, borderRadius: 8, padding: 5, background: err ? 'rgba(94,101,109,0.08)' : T.innerBg }}>
                      {/* Позиція графіка (1-24, як подається в MMS) і поруч
                          астрономічна година — графік і таблиця нижче підписані
                          саме нею, і без підказки позиція 22 читалась як 22:00,
                          хоча це 21:00. */}
                      <div className="text-[10px] mb-1 flex items-baseline justify-between" style={{ color: T.textMuted }}>
                        <span>{String(h).padStart(2, '0')}</span>
                        <span style={{ fontSize: 9, opacity: .7 }}>{String(h - 1).padStart(2, '0')}:00</span>
                      </div>
                      <select value={cur.mode} disabled={!canEdit}
                        onChange={(e) => setHour(h, { mode: e.target.value as Mode })}
                        style={{ width: '100%', background: T.innerBg, color: T.textMain, border: `1px solid ${T.cardBorder}`,
                                 borderRadius: 5, fontSize: 11, padding: '2px', colorScheme: T.inputScheme }}>
                        <option value="idle">—</option>
                        <option value="charge">{t('ak_dsp4_charge_2')}</option>
                        <option value="discharge">{t('ak_dsp4_discharge_2')}</option>
                      </select>
                      <input type="number" value={cur.powerKw || ''} placeholder={t('ak_dsp4_kw')} min={0} step={10} disabled={!canEdit}
                        onChange={(e) => setHour(h, { powerKw: Math.max(0, Number(e.target.value)) })}
                        style={{ width: '100%', marginTop: 3, background: T.innerBg, color: T.textMain, border: `1px solid ${T.cardBorder}`,
                                 borderRadius: 5, fontSize: 11, padding: '2px 4px', colorScheme: T.inputScheme }} />
                      {/* Розрахунковий SOC ПІСЛЯ цієї години — видно, звідки
                          береться обмеження, ще до спроби зберегти. */}
                      {check.trace[h] != null && (
                        <div className="text-[9px] mt-1 text-center"
                          style={{ color: err ? errCol : T.textMuted }}>SOC {n(check.trace[h], 0)}%</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Collapse>
      </div>

      {/* ── підсумковий прогноз ──────────────────────────────────────────── */}
      <div className="mb-8">
      {/* Заголовок винесено ЗА плитку — секційний хедер над карткою. */}
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2.5 px-1">
          <div className="text-base font-bold" style={{ color: T.textMain }}>
            {t('ak_dsp4_net_forecast_solar_generation_bess')}
          </div>
          {/* Кнопки тут свідомо немає: погодинні дані обох віджетів розкриває
              один перемикач у «Графіку заряду/розряду УЗЕ». */}
          <div className="text-[11px]" style={{ color: T.textMuted }}>
            {t('ak_dsp4_solar_charge_discharge', { pv: n(net ? net.pvKwh / 1000 : null), ch: n(net ? net.chargeKwh / 1000 : null), dis: n(net ? net.dischargeKwh / 1000 : null) })}{' '}
            <b style={{ color: C_DISCHARGE }}>{t('ak_dsp4_total_mwh', { n: n(net ? net.netKwh / 1000 : null) })}</b>
          </div>
        </div>

      <div className="rounded-xl p-4 sm:p-5" style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}` }}>
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: PLOT_RIGHT, bottom: 0, left: 0 }} maxBarSize={28} barCategoryGap="16%">
              <CartesianGrid {...ct.gridProps} />
              <XAxis dataKey="hour" {...ct.axis} interval={0} />
              <YAxis width={PLOT_LEFT} {...ct.axis} />
              <Tooltip {...ct.tooltip} cursor={ct.cursorBar}
                formatter={(v: any, nm: any) => [`${Number(v).toFixed(3)} ${t('ak_dsp4_mwh')}`, nm]} />
              {/* Радіус той самий, що на графіку «прогноз vs факт» вище:
                  [3,3,0,0] — заокруглена верхівка, низ лишається на осі. */}
              <Bar dataKey="sun" name={t('ak_dsp4_solar_generation')} fill={ct.series.ok} stroke={ct.series.ok} stackId="net" radius={[3, 3, 0, 0]} {...ct.bar} />
              <Bar dataKey="discharge" name={t('ak_dsp4_bess_discharge')} fill={ct.series.ok2} stroke={ct.series.ok2} stackId="net" radius={[3, 3, 0, 0]} {...ct.bar} />
              <Bar dataKey="charge" name={t('ak_dsp4_bess_charge')} fill={ct.series.warn} stroke={ct.series.warn} stackId="net" radius={[3, 3, 0, 0]} {...ct.bar} />
              <Line dataKey="net" name={t('ak_dsp4_net')} stroke={ct.series.s3} {...ct.line} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <ChartLegend items={[
          { label: t('ak_dsp4_solar_generation'), color: ct.series.ok, bar: true },
          { label: t('ak_dsp4_bess_discharge'), color: ct.series.ok2, bar: true },
          { label: t('ak_dsp4_bess_charge'), color: ct.series.warn, bar: true },
          { label: t('ak_dsp4_net'), color: ct.series.s3 },
        ]} />

        {/* Таблиця повторює розмітку графіка: перша колонка завширшки з віссю Y,
            праворуч той самий відступ, решта — 24 рівні частки поля побудови.
            Тому кожне число стоїть рівно під своєю поділкою осі X. */}
        <Collapse open={hoursOpen && chartData.length > 0}>
          <div style={{ paddingRight: PLOT_RIGHT, paddingTop: 6 }}>
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
              <colgroup>
                <col style={{ width: PLOT_LEFT }} />
                {chartData.map((_, i) => <col key={i} />)}
              </colgroup>
              <tbody>
                <tr>
                  <td style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, textAlign: 'right', paddingRight: 6, whiteSpace: 'nowrap' }}>{t('ak_dsp4_hr')}</td>
                  {chartData.map((d, i) => (
                    <td key={i} style={{ padding: '5px 1px', fontSize: 9, color: T.textDim, textAlign: 'center',
                                         borderLeft: i ? `1px solid ${T.cardBorder}` : 'none' }}>{d.hour}</td>
                  ))}
                </tr>
                <tr>
                  <td style={{ fontSize: 10, color: T.textMuted, fontWeight: 600, textAlign: 'right', paddingRight: 6, whiteSpace: 'nowrap',
                               borderTop: `1px solid ${T.cardBorder}` }}>{t('ak_dsp4_mwh')}</td>
                  {chartData.map((d, i) => (
                    <td key={i} style={{ padding: '5px 1px', fontSize: 10, textAlign: 'center',
                                         color: d.net < 0 ? ct.series.warn : T.textMain,
                                         borderLeft: i ? `1px solid ${T.cardBorder}` : 'none',
                                         borderTop: `1px solid ${T.cardBorder}` }}>
                      {d.net.toFixed(2)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </Collapse>
      </div>
      </div>
    </>
  );
}
